import { useNavigate } from 'react-router-dom';
import { useCallback } from 'react';
import { homeRoute } from '@/config/homeRoute';
import { useShellIdentity } from '@/components/shell/useShellIdentity';

export type NavigationView =
  | 'dashboard'
  | 'patients'
  | 'schedule'
  | 'records'
  | 'lab-orders'
  | 'adt'
  | 'settings'
  | 'add-patient'
  | 'edit-patient'
  | 'patient-timeline'
  | 'patient-prescriptions'
  | 'prescription-details'
  | 'prescriptions'
  | 'new-prescription'
  | 'resource-scheduling'
  | 'provider-availability'
  | 'waitlist-management'
  | 'add-waitlist'
  | 'order-management'
  | 'admin-system-settings'
  | 'admin-system-monitoring'
  | 'admin-audit-logs'
  | 'admin-feature-toggles'
  | 'add-appointment'
  | 'edit-appointment'
  | 'add-record'
  | 'edit-record'
  | 'new-lab-order'
  | 'patient-details'
  | 'appointment-details'
  | 'record-details'
  | 'lab-order-details'
  | 'billing'
  | 'add-invoice'
  | 'edit-invoice'
  | 'view-invoice'
  | 'process-payment'
  | 'templates'
  | 'cpoe'
  | 'imaging-orders'
  | 'imaging-order-details'
  | 'new-imaging-order';

export const useAppNavigation = () => {
  const navigate = useNavigate();

  const { surface } = useShellIdentity();
  const navigateTo = useCallback((view: NavigationView, params?: Record<string, string>) => {
    let path = '/';

    switch (view) {
      case 'dashboard':
        // "dashboard" is this hook's word for HOME, and home moved — see
        // homeRoute. The literal sent every caller to the page the design
        // replaced.
        path = homeRoute(surface);
        break;
      case 'patients':
        path = '/patients';
        if (params?.search) {
          path += `?search=${encodeURIComponent(params.search)}`;
        }
        break;
      case 'patient-details':
        path = params?.patientId ? `/patients/${params.patientId}` : '/patients';
        break;
      case 'add-patient':
        path = '/patients/new';
        break;
      case 'edit-patient':
        path = params?.patientId ? `/patients/${params.patientId}/edit` : '/patients';
        break;
      case 'patient-timeline':
        path = params?.patientId ? `/patients/${params.patientId}?view=timeline` : '/patients';
        break;
      case 'patient-prescriptions':
        path = params?.patientId ? `/patients/${params.patientId}/prescriptions` : '/patients';
        break;
      case 'prescription-details':
        if (params?.patientId && params?.prescriptionId) {
          path = `/patients/${params.patientId}/prescriptions/${params.prescriptionId}`;
        } else if (params?.prescriptionId) {
          path = `/prescriptions/${params.prescriptionId}`;
        } else {
          path = '/prescriptions';
        }
        break;
      case 'prescriptions':
        path = '/prescriptions';
        break;
      case 'new-prescription':
        path = params?.patientId
          ? `/prescriptions/new?patientId=${params.patientId}`
          : '/prescriptions/new';
        break;
      case 'schedule':
        path = '/schedule';
        break;
      case 'resource-scheduling':
        path = '/resource-scheduling';
        break;
      case 'provider-availability':
        path = '/provider-availability';
        break;
      case 'waitlist-management':
        path = '/waitlist-management';
        break;
      case 'add-waitlist':
        path = '/add-waitlist';
        break;
      case 'order-management':
        path = '/order-management';
        break;
      case 'admin-system-settings':
        path = '/admin/system-settings';
        break;
      case 'admin-system-monitoring':
        path = '/admin/system-monitoring';
        break;
      case 'admin-audit-logs':
        path = '/admin/audit-logs';
        break;
      case 'appointment-details':
        path = params?.appointmentId ? `/appointments/${params.appointmentId}` : '/schedule';
        break;
      case 'add-appointment': {
        path = '/appointments/new';
        // Support multiple query params for pre-filling
        const apptParams: string[] = [];
        if (params?.patientId) apptParams.push(`patientId=${params.patientId}`);
        if (params?.date) apptParams.push(`date=${params.date}`);
        if (params?.time) apptParams.push(`time=${params.time}`);
        if (params?.reason) apptParams.push(`reason=${encodeURIComponent(params.reason)}`);
        if (params?.appointmentType) apptParams.push(`type=${params.appointmentType}`);
        if (apptParams.length > 0) {
          path += `?${apptParams.join('&')}`;
        }
        break;
      }
      case 'edit-appointment':
        path = params?.appointmentId ? `/appointments/${params.appointmentId}/edit` : '/schedule';
        break;
      case 'records':
        path = '/records';
        if (params?.patientId) {
          path += `?patientId=${params.patientId}`;
        }
        break;
      case 'record-details':
        path = params?.recordId ? `/records/${params.recordId}` : '/records';
        break;
      case 'add-record': {
        // The encounter flow now lives on the patient page as an overlay.
        // Without a patient, fall back to the patients list for selection.
        if (params?.patientId) {
          const qs: string[] = ['record=new'];
          if (params?.appointmentId) qs.push(`appointmentId=${params.appointmentId}`);
          if (params?.autoStart) qs.push(`autoStart=${params.autoStart}`);
          if (params?.recordingSessionId) qs.push(`recordingSessionId=${params.recordingSessionId}`);
          path = `/patients/${params.patientId}?${qs.join('&')}`;
        } else {
          path = '/patients';
        }
        break;
      }
      case 'edit-record':
        if (params?.recordId && params?.patientId) {
          path = `/patients/${params.patientId}?record=${params.recordId}`;
        } else if (params?.recordId) {
          // No patient context — read-only details page is the safe fallback.
          path = `/records/${params.recordId}`;
        } else {
          path = '/records';
        }
        break;
      case 'lab-orders':
        path = '/lab-orders';
        break;
      case 'lab-order-details':
        path = params?.orderId ? `/lab-orders/${params.orderId}` : '/lab-orders';
        break;
      case 'new-lab-order':
        path = '/lab-orders/new';
        // Support query params for pre-filling patient data
        if (params?.patientId) {
          path += `?patientId=${params.patientId}`;
        }
        break;
      case 'adt':
        path = '/adt';
        break;
      case 'settings':
        path = '/settings';
        break;
      case 'billing':
        path = '/billing';
        break;
      case 'add-invoice':
        path = '/billing/new';
        if (params?.patientId) {
          path += `?patientId=${params.patientId}`;
        }
        break;
      case 'edit-invoice':
        path = params?.id ? `/billing/${params.id}/edit` : '/billing';
        break;
      case 'view-invoice':
        path = params?.id ? `/billing/${params.id}` : '/billing';
        break;
      case 'process-payment':
        path = params?.id ? `/billing/${params.id}/payment` : '/billing';
        break;
      case 'templates':
        path = '/templates';
        break;
      case 'cpoe':
        path = params?.patientId ? `/patients/${params.patientId}/orders` : '/patients';
        break;
      case 'imaging-orders':
        path = '/imaging-orders';
        break;
      case 'imaging-order-details':
        path = params?.orderId ? `/imaging-orders/${params.orderId}` : '/imaging-orders';
        break;
      case 'new-imaging-order':
        path = '/imaging-orders/new';
        if (params?.patientId) {
          path += `?patientId=${params.patientId}`;
        }
        break;
    }

    // Pass params as state for components that need autofill data
    // (e.g., AddAppointmentPage, NewLabOrderPage)
    if (params && Object.keys(params).length > 0) {
      navigate(path, { state: params });
    } else {
      navigate(path);
    }
  }, [navigate]);

  return { navigateTo };
};